import React from 'react';
import type { I18n } from '@lingui/core';
import { useLingui } from '@lingui/react/macro';
import type { LocalBusiness, Review, WithContext } from 'schema-dts';
import serialize from 'serialize-javascript';

import { Locale } from '@rover/constants/src/i18n/lingui.constants';
import { ServiceType } from '@rover/constants/src/translations/service.constants';
import { isAnyRating } from '@rover/react-lib/src/components/modals/Review/utils';
import useI18nContext from '@rover/react-lib/src/context/useI18nContext';
import { useApiPersonDetailReviewsList } from '@rover/rsdk/src/apiClient/latest/api-person-detail-reviews-list';
import type { EnhancedPersonSitterProfile } from '@rover/rsdk/src/apiClient/latest/model/enhancedPersonSitterProfile';
import type { EnhancedPersonSitterProfileService } from '@rover/rsdk/src/apiClient/latest/model/enhancedPersonSitterProfileService';
import type { ReviewDispatch } from '@rover/rsdk/src/apiClient/latest/model/reviewDispatch';
import useBrandSpecificPets from '@rover/shared/js/hooks/useBrandSpecificPets';
import { ServiceAddOnTypeSlugs } from '@rover/types/src/ServiceAddOn';
import { isCatOnlyAvailablePetTypesBasedOnPetTypes } from '@rover/utilities/getAvailablePetTypes/availablePetTypesUtils';
import {
  coercePriceTransparencyLevel,
  getIsPriceTransparency,
} from '@rover/utilities/priceTransparency';

import { getInitialReviewsParams, getLocationString } from '../utils/utils';

import { formatPrice } from './ServiceSection/utils';

const PET_SITTING_ADDITIONAL_TYPE = 'http://www.productontology.org/id/Pet_sitting';
const BEST_RATING = 5;
const WORST_RATING = 1;

export type SitterProfileSchemaInput = {
  sitter: EnhancedPersonSitterProfile;
  imageUrl: string;
  locale: Locale;
  reviews: ReviewDispatch[];
  isCatOnly?: boolean;
};

function getPriceRange(
  service: EnhancedPersonSitterProfileService | undefined,
  locale: Locale,
  i18n: I18n
): string | undefined {
  const standardRate = service?.serviceAddOns.find(
    (sa) => sa.slug === ServiceAddOnTypeSlugs.standardRate && sa.active
  );
  if (!standardRate) return undefined;

  const isFullPrice = getIsPriceTransparency(
    coercePriceTransparencyLevel(service?.priceTransparencyLevel)
  );
  // `unitLabel` arrives from the API as a bare suffix (`/ night`, `each`, or empty).
  return `${formatPrice(i18n, standardRate, locale, isFullPrice)} ${standardRate.unitLabel}`.trim();
}

function buildReviews(reviews: ReviewDispatch[]): Review[] {
  return reviews.flatMap((review) => {
    // Google requires an author and a reviewRating on every Review, and an incomplete
    // nested review invalidates the whole LocalBusiness.
    if (!isAnyRating(review) || !review.poster.shortName || !review.description) return [];

    return [
      {
        '@type': 'Review' as const,
        author: { '@type': 'Person' as const, name: review.poster.shortName },
        datePublished: review.added,
        description: review.description,
        reviewRating: {
          '@type': 'Rating' as const,
          ratingValue: review.overall,
          bestRating: BEST_RATING,
          worstRating: WORST_RATING,
        },
      },
    ];
  });
}

export function buildSitterProfileSchema(
  { sitter, imageUrl, locale, reviews, isCatOnly }: SitterProfileSchemaInput,
  i18n: I18n
): WithContext<LocalBusiness> {
  const url = sitter.canonicalUrl ?? sitter.webUrl;
  const primarySlug = isCatOnly ? ServiceType.DROP_IN : ServiceType.OVERNIGHT_BOARDING;
  const primaryService = sitter.services.find((s) => s.slug === primarySlug);
  const meta = primaryService?.meta;
  const priceRange = getPriceRange(primaryService, locale, i18n);
  const areaServed = getLocationString(meta);
  const ratingValue = Number.parseFloat(sitter.ratingsAverage);
  const hasRating = sitter.reviewsCount > 0 && ratingValue > 0;
  const reviewNodes = buildReviews(reviews);

  return {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    '@id': url,
    name: sitter.shortName,
    url,
    image: imageUrl,
    additionalType: PET_SITTING_ADDITIONAL_TYPE,
    ...(priceRange ? { priceRange } : {}),
    ...(areaServed ? { areaServed } : {}),
    ...(meta?.city && meta?.state
      ? {
          address: {
            '@type': 'PostalAddress',
            ...(meta.neighborhood ? { name: meta.neighborhood } : {}),
            addressLocality: meta.city,
            addressRegion: meta.state,
            ...(meta.countryCode ? { addressCountry: meta.countryCode } : {}),
            ...(meta.zip ? { postalCode: meta.zip } : {}),
          },
        }
      : {}),
    ...(meta?.latitude && meta?.longitude
      ? {
          geo: {
            '@type': 'GeoCoordinates',
            latitude: Number(meta.latitude),
            longitude: Number(meta.longitude),
          },
        }
      : {}),
    ...(hasRating
      ? {
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue,
            reviewCount: sitter.reviewsCount,
            bestRating: BEST_RATING,
            worstRating: WORST_RATING,
          },
        }
      : {}),
    ...(reviewNodes.length > 0 ? { review: reviewNodes } : {}),
    potentialAction: {
      '@type': 'ReserveAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: url,
        actionPlatform: [
          'https://schema.org/DesktopWebPlatform',
          'https://schema.org/IOSPlatform',
          'https://schema.org/AndroidPlatform',
        ],
      },
      result: {
        '@type': 'Reservation',
        name: isCatOnly ? 'Book a cat sitter' : 'Book a pet sitter',
      },
    },
  };
}

export type Props = Pick<SitterProfileSchemaInput, 'sitter' | 'imageUrl'>;

const SitterProfileJsonLd: React.FC<Props> = ({ sitter, imageUrl }) => {
  const { i18n } = useLingui();
  const { locale } = useI18nContext();
  const isCatOnly = isCatOnlyAvailablePetTypesBasedOnPetTypes(useBrandSpecificPets());
  // Same query key as the page's prefetch and review sections, so this reads their cache.
  const { data } = useApiPersonDetailReviewsList(sitter.opk, getInitialReviewsParams());

  const schema = buildSitterProfileSchema(
    { sitter, imageUrl, locale, isCatOnly, reviews: data?.results ?? [] },
    i18n
  );

  return (
    <script
      type="application/ld+json"
      data-testid="sitter-profile-json-ld"
      // Member-supplied values land inside a raw text element, where `</script` would
      // end it early. `serialize` escapes those characters to JSON escapes, which
      // keeps the payload inert and still parseable as JSON-LD.
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: serialize(schema, { isJSON: true }) }}
    />
  );
};

export default SitterProfileJsonLd;
